import { useState, useCallback } from 'react'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'
import KorektyService from '@/services/KorektyService'

const NOOP = () => {}

export function useKorektaActions(loadInvoicesData = NOOP) {
	const { t } = useTranslation()
	const [isDeleting, setIsDeleting] = useState(false)
	const [assignInvoiceId, setAssignInvoiceId] = useState(null)
	const [isAssigning, setIsAssigning] = useState(false)

	const openAssignPlacowka = useCallback(invoiceId => {
		if (!invoiceId || isAssigning) return
		setAssignInvoiceId(invoiceId)
	}, [isAssigning])

	const closeAssignPlacowka = useCallback(() => {
		if (isAssigning) return
		setAssignInvoiceId(null)
	}, [isAssigning])

	const handleAssignPlacowka = useCallback(
		async placowka => {
			if (isAssigning || !assignInvoiceId) return

			const placowkaId = placowka?._id || placowka?.id
			if (!placowkaId) {
				await Swal.fire({
					title: t('korekty.list.assignPlacowkaModal.errorTitle'),
					text: t('korekty.list.assignPlacowkaModal.invalidPlacowka'),
					icon: 'error',
				})
				return
			}

			setIsAssigning(true)
			try {
				const { success, error } = await KorektyService.assignCustomer(assignInvoiceId, placowkaId)

				if (success) {
					setAssignInvoiceId(null)
					await Swal.fire({
						title: t('korekty.list.assignPlacowkaModal.successTitle'),
						text: t('korekty.list.assignPlacowkaModal.successText'),
						icon: 'success',
						timer: 1200,
						showConfirmButton: false,
					})
					await Promise.resolve(loadInvoicesData())
				} else {
					await Swal.fire({
						title: t('korekty.list.assignPlacowkaModal.errorTitle'),
						text: error || t('korekty.list.assignPlacowkaModal.errorFallback'),
						icon: 'error',
					})
				}
			} catch (err) {
				console.error('Assign placowka error:', err)
				await Swal.fire({
					title: t('korekty.list.assignPlacowkaModal.errorTitle'),
					text: err?.message || t('korekty.list.assignPlacowkaModal.errorFallback'),
					icon: 'error',
				})
			} finally {
				setIsAssigning(false)
			}
		},
		[assignInvoiceId, isAssigning, loadInvoicesData, t]
	)

	// Single stable handler; prevents double-submit with isDeleting guard
	const handleDeleteSelected = useCallback(
		async (selectedInvoices, clearSelection = NOOP) => {
			if (isDeleting) return

			const ids = Array.isArray(selectedInvoices)
				? selectedInvoices
				: selectedInvoices instanceof Set
				? Array.from(selectedInvoices)
				: []

			if (ids.length === 0) {
				await Swal.fire({
					title: t('korekty.list.deleteDialog.noneSelectedTitle'),
					text: t('korekty.list.deleteDialog.noneSelectedText'),
					icon: 'warning',
				})
				return
			}

			const n = ids.length
			const result = await Swal.fire({
				title: t('korekty.list.deleteDialog.confirmTitle'),
				text: t('korekty.list.deleteDialog.confirmText', { count: n }),
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#d33',
				cancelButtonColor: '#3085d6',
				confirmButtonText: t('korekty.list.deleteDialog.confirmButton'),
				cancelButtonText: t('korekty.list.deleteDialog.cancelButton'),
			})
			if (!result.isConfirmed) return

			setIsDeleting(true)
			try {
				const { success, error, message } = await KorektyService.deleteKorekty(ids)

				if (success) {
					await Swal.fire({
						title: t('korekty.list.deleteDialog.deletedTitle'),
						text: message || t('korekty.list.deleteDialog.deletedSuccess'),
						icon: 'success',
						timer: 1200,
						showConfirmButton: false,
					})
					// Reset UI state then refresh data
					clearSelection()
					await Promise.resolve(loadInvoicesData())
				} else {
					await Swal.fire({
						title: t('korekty.list.deleteDialog.errorTitle'),
						text: error || t('korekty.list.deleteDialog.errorFallback'),
						icon: 'error',
					})
				}
			} catch (err) {
				console.error('Delete invoices error:', err)
				await Swal.fire({
					title: t('korekty.list.deleteDialog.errorTitle'),
					text: err?.message || t('korekty.list.deleteDialog.unexpectedError'),
					icon: 'error',
				})
			} finally {
				setIsDeleting(false)
			}
		},
		[isDeleting, loadInvoicesData, t]
	)

	return {
		isDeleting,
		handleDeleteSelected,
		assignInvoiceId,
		isAssigning,
		openAssignPlacowka,
		closeAssignPlacowka,
		handleAssignPlacowka,
	}
}
